import { FilterState, Message } from "../../../state/AppState.ts";
import { HttpClient, Result } from "../../../../http/HttpClient.ts";

type CitationReferenceItem = {
  chunkInfo: {
    content: string;
    documentMetadata: {
      document: string;
      uri: string;
      title: string;
      pageIdentifier: string | null;
      structData?: {
        source_document_uri?: string;
        contextual_relevancy?: number;
      };
    };
    relevanceScore?: number;
  };
  unstructuredDocumentInfo?: unknown;
  structuredDocumentInfo?: unknown;
};

type CitationsResponse = {
  message_id?: string;
  /**
   * @deprecated Use `references` instead. This field is kept for backward compatibility.
   */
  citation_references?: CitationReferenceItem[];
  references?: CitationReferenceItem[];
  citations?: {
    startIndex: string;
    endIndex: string;
    sources: {
      referenceId: string;
    }[];
  }[];
};

type RelatedQuestionAction = {
  type: "submit-related-question";
  label: string;
  parameters: {
    query: string;
  };
};

type GenericAction = {
  type: string;
  label: string;
  parameters: Record<string, string>;
};

type MessageResponse = {
  messages?: {
    message_id: string;
    content: string;
    sender: "user" | "agent";
    timestamp: string;
    thought?: boolean;
    actions?: (RelatedQuestionAction | GenericAction)[];
    applied_filters?: FilterState;
  }[];
};

export class MessageApiService {
  constructor(private readonly httpClient: HttpClient) {}

  async submitMessage(payload: {
    researchId: string;
    message: string;
  }): Result<{
    responseId: string;
  }> {
    const response = await this.httpClient.post<{
      message_id: string;
    }>(`/research/${payload.researchId}/messages/`, {
      query: payload.message,
    });
    return {
      success: response.success,
      data: response.data
        ? { responseId: response.data.message_id }
        : undefined,
      httpStatus: response.httpStatus,
    };
  }

  async getResearchMessages(payload: { researchId: string }): Result<{
    messages: Message[];
  }> {
    const [messageResponse, citationsResponse] = await Promise.all([
      this.httpClient.get<MessageResponse>(
        `/research/${payload.researchId}/messages/`,
      ),
      this.httpClient.get<CitationsResponse>(
        `/research/${payload.researchId}/references/`,
      ),
    ]);
    const messages = messageResponse.data?.messages;
    const {
      citations,
      references,
      messageId: citationsMessageId,
    } = this._transformCitationsResponse(citationsResponse.data);

    return {
      success: messageResponse.success,
      data: messages && {
        messages: messages.map((message) => {
          const isCitationMessage = message.message_id === citationsMessageId;
          return {
            id: message.message_id,
            value: message.content,
            status: "completed",
            sender: message.sender,
            create_time: message.timestamp,
            thought: message.thought,
            citations: isCitationMessage ? citations : [],
            references: isCitationMessage ? references : [],
            appliedFilters: message.applied_filters,
            actions: message.actions?.filter(
              (action): action is RelatedQuestionAction => {
                return action.type === "submit-related-question";
              },
            ),
          };
        }),
      },
      httpStatus: messageResponse.httpStatus,
    };
  }

  async getMessage(payload: {
    researchId: string;
    messageId: string;
  }): Result<{ message: Message }> {
    const response = await this.httpClient.get<{
      message_id: string;
      content: string;
      sender: "user" | "agent";
      timestamp: string;
      thought?: boolean;
      actions?: (RelatedQuestionAction | GenericAction)[];
      applied_filters?: FilterState;
    }>(`/research/${payload.researchId}/messages/${payload.messageId}`);

    if (!response.success || !response.data) {
      return {
        success: response.success,
        data: undefined,
        httpStatus: response.httpStatus,
      };
    }

    const message = response.data;
    return {
      success: true,
      data: {
        message: {
          id: message.message_id,
          value: message.content,
          status: "completed",
          sender: message.sender,
          create_time: message.timestamp,
          thought: message.thought,
          citations: [],
          references: [],
          appliedFilters: message.applied_filters,
          actions: message.actions?.filter(
            (action): action is RelatedQuestionAction => {
              return action.type === "submit-related-question";
            },
          ),
        },
      },
      httpStatus: response.httpStatus,
    };
  }

  private _transformCitationsResponse(citationsResponse?: CitationsResponse) {
    const messageId = citationsResponse?.message_id;
    const citations: Message["citations"] = Array.isArray(
      citationsResponse?.citations,
    )
      ? citationsResponse.citations.map((citation) => ({
          pointer: parseInt(citation.endIndex),
          referenceIds: citation.sources.map((source) =>
            (parseInt(source.referenceId) + 1).toString(),
          ),
        }))
      : [];

    // Support both old format (citation_references) and new format (references)
    const rawReferences =
      citationsResponse?.citation_references || citationsResponse?.references;

    const references: Message["references"] = Array.isArray(rawReferences)
      ? rawReferences.map((citationReference, index) => ({
          id: (index + 1).toString(),
          title: citationReference.chunkInfo.documentMetadata.title,
          page:
            citationReference.chunkInfo.documentMetadata.pageIdentifier || "",
          excerpt: citationReference.chunkInfo.content,
          sourceUrl:
            citationReference.chunkInfo.documentMetadata.structData
              ?.source_document_uri ||
            citationReference.chunkInfo.documentMetadata.uri,
        }))
      : [];
    return { citations, references, messageId };
  }
}
